
import React, { useState } from 'react';
import { 
  Users, DollarSign, CheckCircle, XCircle, AlertCircle, Search, Filter, FileText
} from 'lucide-react';
import { TransactionStatus } from '../types';
import { useSignalData } from '../context/SignalContext';

export const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'PAYOUTS' | 'TRANSACTIONS'>('PAYOUTS');
  const { payouts, transactions, approvePayout, rejectPayout, markPayoutPaid, updateTransaction } = useSignalData();
  const [searchTerm, setSearchTerm] = useState('');

  const getStatusBadge = (status: TransactionStatus) => {
    switch (status) {
      case TransactionStatus.PENDING:
        return <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-md text-xs font-semibold flex items-center w-fit"><AlertCircle className="w-3 h-3 mr-1"/> Pending</span>;
      case TransactionStatus.APPROVED:
        return <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-md text-xs font-semibold flex items-center w-fit"><CheckCircle className="w-3 h-3 mr-1"/> Approved</span>;
      case TransactionStatus.PAID:
        return <span className="bg-green-100 text-green-700 px-2 py-1 rounded-md text-xs font-semibold flex items-center w-fit"><DollarSign className="w-3 h-3 mr-1"/> Paid</span>;
      case TransactionStatus.REJECTED:
        return <span className="bg-red-100 text-red-700 px-2 py-1 rounded-md text-xs font-semibold flex items-center w-fit"><XCircle className="w-3 h-3 mr-1"/> Rejected</span>;
    }
  };

  const filteredPayouts = payouts.filter(p => 
    p.affiliateName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTransactions = transactions.filter(t => 
    t.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || 
    t.product.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Admin Control Panel</h2>
          <p className="text-slate-500">Manage affiliates, review commissions, and process payouts.</p>
        </div>
        <div className="flex bg-white p-1 rounded-lg shadow-sm border border-slate-200">
          <button 
            onClick={() => setActiveTab('OVERVIEW')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'OVERVIEW' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Overview
          </button>
           <button 
             onClick={() => setActiveTab('TRANSACTIONS')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'TRANSACTIONS' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Commissions <span className="ml-1 bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full text-xs">
              {transactions.filter(t => t.status === TransactionStatus.PENDING).length}
            </span>
          </button>
          <button 
             onClick={() => setActiveTab('PAYOUTS')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'PAYOUTS' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Payouts <span className="ml-1 bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full text-xs">
              {payouts.filter(p => p.status === TransactionStatus.PENDING).length}
            </span>
          </button>
        </div>
      </div>

      {activeTab === 'PAYOUTS' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row gap-4 justify-between items-center bg-slate-50">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-brand-600" />
              Yêu cầu rút tiền
            </h3>
            <div className="flex gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Tìm theo tên Affiliate..." 
                  className="w-full pl-9 pr-4 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50 text-slate-700 font-medium border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4">Request ID</th>
                  <th className="px-6 py-4">Affiliate</th>
                  <th className="px-6 py-4">Wallet Details</th>
                  <th className="px-6 py-4 text-right">Amount (USD)</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredPayouts.map((payout) => (
                  <tr key={payout.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 font-mono text-xs">{payout.id}</td>
                    <td className="px-6 py-4 font-medium text-slate-800">{payout.affiliateName}<br/><span className="text-xs text-slate-400 font-normal">{payout.date}</span></td>
                    <td className="px-6 py-4 max-w-xs truncate" title={payout.walletAddress}>
                      <div className="flex items-center gap-2">
                         <span className="bg-slate-100 p-1 rounded font-mono text-xs">{payout.walletAddress}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right font-bold text-slate-800">${payout.amount}</td>
                    <td className="px-6 py-4">{getStatusBadge(payout.status)}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {payout.status === TransactionStatus.PENDING && (
                          <>
                            <button 
                              onClick={() => approvePayout(payout.id)}
                              className="text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1.5 rounded border border-blue-200 font-medium transition-colors"
                            >
                              Approve
                            </button>
                            <button 
                              onClick={() => rejectPayout(payout.id)}
                              className="text-xs bg-red-50 text-red-600 hover:bg-red-100 px-3 py-1.5 rounded border border-red-200 font-medium transition-colors"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {payout.status === TransactionStatus.APPROVED && (
                           <button 
                           onClick={() => markPayoutPaid(payout.id)}
                           className="text-xs bg-emerald-600 text-white hover:bg-emerald-700 px-3 py-1.5 rounded font-medium shadow-sm transition-all"
                         >
                           Mark as Paid
                         </button>
                        )}
                         {payout.status === TransactionStatus.PAID && (
                           <span className="text-xs text-slate-400">Completed</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'TRANSACTIONS' && (
         <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row gap-4 justify-between items-center bg-slate-50">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <FileText className="w-5 h-5 text-brand-600" />
              Đơn hàng & Hoa hồng
            </h3>
            <div className="flex gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Tìm theo sản phẩm/khách..." 
                  className="w-full pl-9 pr-4 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50 text-slate-700 font-medium border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4">Transaction ID</th>
                  <th className="px-6 py-4">Customer</th>
                  <th className="px-6 py-4">Product</th>
                  <th className="px-6 py-4 text-right">Value</th>
                  <th className="px-6 py-4 text-right">Commission</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTransactions.map((txn) => (
                  <tr key={txn.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 font-mono text-xs">{txn.id}</td>
                    <td className="px-6 py-4 font-medium text-slate-800">{txn.customerName}<br/><span className="text-xs text-slate-400 font-normal">{txn.date}</span></td>
                    <td className="px-6 py-4">
                        <span className="bg-slate-100 px-2 py-1 rounded text-xs">{txn.product}</span>
                    </td>
                    <td className="px-6 py-4 text-right text-slate-600">${txn.amount}</td>
                    <td className="px-6 py-4 text-right font-bold text-green-600">+${txn.commission}</td>
                    <td className="px-6 py-4">{getStatusBadge(txn.status)}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {txn.status === TransactionStatus.PENDING && (
                          <>
                            <button 
                              onClick={() => updateTransaction({...txn, status: TransactionStatus.APPROVED})}
                              className="text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1.5 rounded border border-blue-200 font-medium transition-colors"
                            >
                              Approve
                            </button>
                            <button 
                              onClick={() => updateTransaction({...txn, status: TransactionStatus.REJECTED})}
                              className="text-xs bg-red-50 text-red-600 hover:bg-red-100 px-3 py-1.5 rounded border border-red-200 font-medium transition-colors"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {txn.status !== TransactionStatus.PENDING && (
                           <span className="text-xs text-slate-400">-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'OVERVIEW' && (
        <div className="bg-white p-12 text-center rounded-xl border border-slate-200 border-dashed">
            <h3 className="text-slate-400">Global Analytics Placeholder</h3>
            <p className="text-sm text-slate-400 mt-2">Charts showing total system revenue and top affiliates would go here.</p>
        </div>
      )}
    </div>
  );
};
